import { UIOption } from './ui-option';
import { Component } from '../../decorators/component.decorator';
import type { Attributes } from '../ui-element.type';
import styles from './ui-option-basic.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIOptionBasic
 * @element ui-option-basic
 * @classdesc Represents a class for <code>ui-option-basic</code> element.
 * A text-only option that displays its <code>text</code> or <code>value</code> as a
 * plain label. Used with <code>ui-dropdown layout="basic"</code> and
 * <code>ui-option-list</code>.
 * @example
 * <ui-dropdown layout="basic">
 *   <select>
 *     <option value="first">First</option>
 *     <option value="second">Second</option>
 *   </select>
 * </ui-dropdown>
 * @example
 * <ui-option-list label="Select option">
 *   <ui-option-basic optgroup text="Group A"></ui-option-basic>
 *   <ui-option-basic value="a1" text="Alpha One"></ui-option-basic>
 *   <ui-option-basic value="a2" selected text="Alpha Two"></ui-option-basic>
 * </ui-option-list>
 */

@Component({
    tag: 'ui-option-basic',
    styles,
})
export class UIOptionBasic extends UIOption {
    /**
     * Finds match in option value and given texts.
     * @param {HTMLOptionElement} option
     * @param {string} text
     * @returns {boolean}
     */
    findMatch(option: HTMLOptionElement, text: string): boolean {
        return (
            option.getAttribute('value')?.match(new RegExp(text, 'i')) !== null
        );
    }

    /**
     * For basic option returns its own dataset only.
     * @returns {Attributes}
     */
    getData(): Attributes {
        return this.getAttributes();
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.insertElements(
            this.optgroup
                ? [this.buildOptGroupLabel()]
                : [
                      {
                          tagName: 'span',
                          classList: { 'ui-option__label': true },
                          children: [
                              this.getAttribute('text') ||
                                  this.getAttribute('value') ||
                                  '',
                          ],
                      },
                  ]
        );

        super.render();
    }
}
